import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaUserShield, FaUserCog, FaUsers, FaPlus, FaBan, FaExchangeAlt, FaTrash, FaLaptop, FaSitemap, FaCheck, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { authFetch } from '../utils/api';
import usePolling from '../hooks/usePolling';

export default function AdminConsole() {
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  const [showAddMember, setShowAddMember] = useState(false);
  const [position, setPosition] = useState('');
  const [studentId, setStudentId] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const [membersByRole, setMembersByRole] = useState({});
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');

  // Fetch all members and group by role. `silent` skips the loading state
  // so the background poll below doesn't flash the list every refresh.
  async function fetchMembers(silent = false) {
    if (!silent) setMembersLoading(true);
    setMembersError('');
    try {
      const response = await authFetch('https://dev.sliitleo.org/Backend/rolechange.php');
      if (!response.ok) throw new Error('Failed to fetch members');
      const members = await response.json();
      // Group by role. Normalizes the "Club Performence Evaluator" typo
      // (still used by some existing records) onto the correctly-spelled
      // "Club Performance Evaluator" -- the section list below only
      // renders the correct spelling, so a member stored under the typo'd
      // one was silently invisible in that section even though they had
      // the role. New assignments from this page now save the corrected
      // spelling (see the two <select> options below), so this fallback
      // only matters for records written before that fix.
      const grouped = {};
      members.forEach(m => {
        let role = m.Role || 'Unassigned';
        if (role === 'Club Performence Evaluator') role = 'Club Performance Evaluator';
        if (!grouped[role]) grouped[role] = [];
        grouped[role].push(m);
      });
      setMembersByRole(grouped);
    } catch (err) {
      setMembersError(err.message);
    } finally {
      if (!silent) setMembersLoading(false);
    }
  }

  React.useEffect(() => {
    fetchMembers();
  }, []);

  // Live-refresh the member/role list in the background so a role change
  // made elsewhere (or by another admin) shows up without a manual reload.
  usePolling(() => fetchMembers(true), { intervalMs: 20000, hiddenIntervalMs: 60000 });

  async function handleAddMember(e) {
    e.preventDefault();
    setAddError('');
    if (!position || !studentId) {
      setAddError('Please fill all fields');
      return;
    }
    setAddLoading(true);
    try {
      const response = await authFetch('https://dev.sliitleo.org/Backend/rolechange.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          studentId,
          role: position
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowAddMember(false);
        setStudentId('');
        setPosition('');
        toast.success('Role updated successfully!', {
          duration: 4000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        });
        fetchMembers(); // Refresh list
      } else {
        const errorMsg = data.error || data.message || 'Failed to update role.';
        setAddError(errorMsg);
        toast.error(errorMsg, {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        });
      }
    } catch (err) {
      setAddError('Server error: ' + err.message);
    } finally {
      setAddLoading(false);
    }
  }

  // State for Change Role modal
  const [showChangeRole, setShowChangeRole] = useState(false);
  const [changeRoleMember, setChangeRoleMember] = useState(null);
  const [changeRoleValue, setChangeRoleValue] = useState("");
  const [changeRoleLoading, setChangeRoleLoading] = useState(false);
  const [changeRoleError, setChangeRoleError] = useState("");
  const [banConfirmation, setBanConfirmation] = useState(null);

  const [showBanConfirm, setShowBanConfirm] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [isBanning, setIsBanning] = useState(false);

  function openChangeRoleModal(member) {
    setChangeRoleMember(member);
    setChangeRoleValue(member.Role || member.role || "");
    setShowChangeRole(true);
    setChangeRoleError("");
  }
  function closeChangeRoleModal() {
    setShowChangeRole(false);
    setChangeRoleMember(null);
    setChangeRoleValue("");
    setChangeRoleError("");
  }

  async function updateUserRole(studentId, role, memberName = '') {
    const loadingToast = toast.loading('Updating role...', {
      position: 'top-center'
    });

    try {
      const response = await authFetch('https://dev.sliitleo.org/Backend/rolechange.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          studentId,
          role
        })
      });
      const data = await response.json();

      if (data.success) {
        const successMessage = memberName
          ? `Successfully updated ${memberName}'s role to ${role}`
          : 'Role updated successfully!';

        toast.dismiss(loadingToast);
        toast.success(successMessage, {
          duration: 4000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        });

        fetchMembers();
        return true;
      } else {
        const errorMsg = data.error || data.message || 'Failed to update role.';
        toast.dismiss(loadingToast);
        toast.error(errorMsg, {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        });
        return false;
      }
    } catch (err) {
      alert('Server error: ' + err.message);
      return false;
    }
  }

  async function handleChangeRoleSubmit(e) {
    e.preventDefault();
    setChangeRoleError("");
    if (!changeRoleValue) {
      setChangeRoleError("Please select a role");
      return;
    }
    setChangeRoleLoading(true);
    const success = await updateUserRole(changeRoleMember.SLIITID, changeRoleValue);
    if (success) {
      closeChangeRoleModal();
    }
    setChangeRoleLoading(false);
  }

  async function handleMakeClubMember(member) {
    const confirmMessage = `Are you sure you want to make ${member.FullName} a Club Member?`;

    const userConfirmed = await new Promise((resolve) => {
      toast((t) => (
        <div className="text-center">
          <p className="mb-4 text-gray-800">{confirmMessage}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Yes, Update
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ), {
        duration: 10000,
        style: {
          minWidth: '300px',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
      });
    });

    if (userConfirmed) {
      await updateUserRole(member.SLIITID, 'Club Member', member.FullName);
    }
  }

  const handleBanUser = async (member) => {
    setUserToBan(member);
    setShowBanConfirm(true);
  };

  const confirmBan = async () => {
    if (!userToBan) return;

    const toastId = toast.loading('Banning user...');
    setBanConfirmation(userToBan.SLIITID);
    setIsBanning(true);

    try {
      const formData = new URLSearchParams();
      formData.append('studentId', userToBan.SLIITID);
      formData.append('role', 'Banned');

      let response;
      try {
        response = await authFetch('https://dev.sliitleo.org/Backend/rolechange.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData,
          credentials: 'include'
        });
      } catch (error) {
        response = await authFetch('https://dev.sliitleo.org/Backend/rolechange.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to ban user');
      }

      const data = await response.json();
      if (data.success) {
        toast.success(`Successfully Banned ${userToBan.Name || userToBan.name || 'the user'}`, { id: toastId });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        throw new Error(data.error || 'Failed to ban user');
      }
    } catch (error) {
      console.error('Error banning user:', error);
      toast.error(error.message || 'Failed to ban user', { id: toastId });
    } finally {
      setBanConfirmation(null);
      setIsBanning(false);
      setShowBanConfirm(false);
      setUserToBan(null);
    }
  };

  const BanConfirmationModal = () => {
    if (!showBanConfirm) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-panel panel">
          <div className="modal-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span
                style={{
                  width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                  background: 'rgba(226,100,92,.12)', display: 'grid', placeItems: 'center', color: 'var(--error)'
                }}
              >
                <FaBan size={16} />
              </span>
              <div style={{ minWidth: 0 }}>
                <h3>Confirm Ban</h3>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>This action cannot be undone</div>
              </div>
            </div>
            <button
              className="icon-btn"
              onClick={() => { setShowBanConfirm(false); setUserToBan(null); }}
              disabled={isBanning}
              aria-label="Close ban confirmation dialog"
            >
              <FaTimes size={14} />
            </button>
          </div>

          <div className="modal-body">
            <p style={{ color: 'var(--text-2)', margin: 0 }}>
              Are you sure you want to ban{' '}
              <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>
                {userToBan?.Name || userToBan?.name || 'this user'}
              </span>
              ? This will revoke their access to the portal.
            </p>
          </div>

          <div className="modal-foot">
            <button
              className="btn btn-ghost"
              onClick={() => { setShowBanConfirm(false); setUserToBan(null); }}
              disabled={isBanning}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={confirmBan}
              disabled={isBanning}
            >
              {isBanning ? 'Banning…' : (<><FaBan size={13} /> Yes, Ban User</>)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---- Presentation-only derived summary stats (no new fetches / state) ----
  const totalMembers = Object.values(membersByRole).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
    0
  );
  const bannedCount = membersByRole['Banned']?.length || 0;
  const adminCount = (membersByRole['System Admin']?.length || 0) + (membersByRole['President']?.length || 0);
  const activeRoleCount = Object.keys(membersByRole).filter(
    r => r !== 'Banned' && (membersByRole[r]?.length || 0) > 0
  ).length;

  const quickLinks = [
    { to: '/admin', label: 'Role Management', icon: <FaUserCog size={15} /> },
    { to: '/session-management', label: 'Session Management', icon: <FaUsers size={15} /> },
    { to: '/device-management', label: 'Device Management', icon: <FaLaptop size={15} /> },
    { to: '/path-management', label: 'Role Path Management', icon: <FaSitemap size={15} /> },
  ];

  return (
    <>
      <Toaster />
      <div className="flex min-h-screen">
        <Sidebar />
        <Topbar user={user} />

        <main className="lg:ml-64 mt-14 ml-0 w-full">
          <div className="content">
          {/* Page head */}
          <div className="page-head">
            <div>
              <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaUserShield style={{ color: 'var(--accent)' }} size={20} />
                Admin Console
              </h1>
              <p className="page-desc">Role management and member administration</p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowAddMember(true)}>
              <FaPlus size={13} /> Add Member
            </button>
          </div>

          {/* Summary tiles */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 'var(--sp-4)',
              marginBottom: 'var(--sp-6)'
            }}
          >
            <div className="panel tile">
              <span className="tile-label">Total Members</span>
              <span className="tile-value num">
                {membersLoading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : totalMembers}
              </span>
              <span className="tile-trend trend-flat">All recorded users</span>
            </div>
            <div className="panel tile">
              <span className="tile-label">Active Roles</span>
              <span className="tile-value num">
                {membersLoading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : activeRoleCount}
              </span>
              <span className="tile-trend trend-flat">Roles with at least 1 member</span>
            </div>
            <div className="panel tile">
              <span className="tile-label">System Admins</span>
              <span className="tile-value num">
                {membersLoading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : adminCount}
              </span>
              <span className="tile-trend trend-up">System Admin + President</span>
            </div>
            <div className="panel tile">
              <span className="tile-label">Banned Users</span>
              <span className="tile-value num">
                {membersLoading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : bannedCount}
              </span>
              <span className={bannedCount > 0 ? 'tile-trend trend-warn' : 'tile-trend trend-flat'}>
                {bannedCount > 0 ? 'Access revoked' : 'None banned'}
              </span>
            </div>
          </div>

          {/* Quick links to admin sub-pages */}
          <style>{`
            .admin-quicklink { text-decoration: none; color: var(--text-1); transition: background .15s var(--ease), border-color .15s var(--ease); }
            .admin-quicklink:hover { background: var(--surface-2); border-color: var(--border-strong); }
          `}</style>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--sp-4)',
              marginBottom: 'var(--sp-6)'
            }}
          >
            {quickLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="panel admin-quicklink"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'var(--sp-4)' }}
              >
                <span
                  style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                    background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: 'var(--accent)'
                  }}
                >
                  {link.icon}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Members by role */}
          <div className="eyebrow" style={{ marginBottom: 'var(--sp-3)' }}>Members by Role</div>

          {membersLoading ? (
            <div className="panel" style={{ padding: 'var(--sp-6)', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-2)' }}>
              <span className="spinner" /> Loading members…
            </div>
          ) : membersError ? (
            <div className="error-banner">{membersError}</div>
          ) : (
            [
              "System Admin",
              "President",
              "Vice President",
              "Secretary",
              "Treasurer",
              "Co - Asst. Secretary",
              "Co - Asst. Treasurer",
              "Chief Editor",
              "President Program Coordinator",
              "Club Performance Evaluator",
              "Club Director",
              "Team Head",
              "Activity Director",
              "Past President",
              "Past Board Member",
              "IT Department",
              "Installation sub-committee member",
              "Reporting Sub-Committee",
              "Committee Member",
              "Banned"
            ].map(role => {
              const list = membersByRole[role] || [];
              return (
                <div key={role} className="panel" style={{ marginBottom: 'var(--sp-5)' }}>
                  <div className="section-head">
                    <h2>{role}(s)</h2>
                    <span className="chip">{list.length} member{list.length === 1 ? '' : 's'}</span>
                  </div>

                  {list.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Member</th>
                            <th>SLIIT ID</th>
                            <th>Club ID</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map(member => (
                            <tr key={member.SLIITID}>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                  <span
                                    style={{
                                      width: 28, height: 28, borderRadius: '999px', flexShrink: 0,
                                      background: 'var(--surface-2)', border: '1px solid var(--border)',
                                      display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12,
                                      color: 'var(--text-1)'
                                    }}
                                  >
                                    {member.FullName ? member.FullName[0] : '?'}
                                  </span>
                                  <span className="truncate" title={member.FullName} style={{ fontWeight: 600 }}>
                                    {member.FullName}
                                  </span>
                                </div>
                              </td>
                              <td className="num">{member.SLIITID}</td>
                              <td className="num">LCSM{member.SLIITLeoID}</td>
                              <td>
                                <span className="pill pill-success">
                                  <span className="dot" /> Active
                                </span>
                              </td>
                              <td>
                                <div className="row-actions">
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => setUserToBan(member) || setShowBanConfirm(true)}
                                    aria-label={
                                      banConfirmation === member.SLIITID
                                        ? `Confirm ban for ${member.FullName}`
                                        : `Ban ${member.FullName}`
                                    }
                                  >
                                    {banConfirmation === member.SLIITID
                                      ? (<><FaCheck size={12} /> Confirm</>)
                                      : (<><FaBan size={12} /> Ban</>)}
                                  </button>

                                  <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => handleMakeClubMember(member)}
                                    aria-label={`Make ${member.FullName} a club member`}
                                  >
                                    <FaUserCog size={12} /> Member
                                  </button>

                                  <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => openChangeRoleModal(member)}
                                    aria-label={`Change role for ${member.FullName}`}
                                  >
                                    <FaExchangeAlt size={12} /> Role
                                  </button>

                                  <button
                                    className="btn btn-sm btn-danger"
                                    aria-label={`Remove ${member.FullName}`}
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <span className="empty-icon"><FaUsers /></span>
                      <p>No users found in this role.</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add Member Modal */}
        {showAddMember && (
          <div className="modal-overlay">
            <div className="modal-panel panel">
              <div className="modal-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: 'var(--link)'
                    }}
                  >
                    <FaUserCog size={16} />
                  </span>
                  <h3 className="truncate">
                    Add Member <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>/ Role Management</span>
                  </h3>
                </div>
                <button className="icon-btn" onClick={() => setShowAddMember(false)} aria-label="Close add member dialog">
                  <FaTimes size={14} />
                </button>
              </div>

              <form onSubmit={handleAddMember}>
                <div className="modal-body">
                  <div className="field">
                    <label className="field-label" htmlFor="add-member-position">Select Position</label>
                    <select
                      id="add-member-position"
                      value={position}
                      onChange={e => setPosition(e.target.value)}
                      disabled={addLoading}
                    >
                      <option value="">Select Position</option>

                      <option value="System Admin">System Admin</option>
                      <option value="President">President</option>
                      <option value="Vice President">Vice President</option>
                      <option value="Secretary">Secretary</option>
                      <option value="Treasurer">Treasurer</option>
                      <option value="Co - Asst. Secretary">Co - Asst. Secretary</option>
                      <option value="Co - Asst. Treasurer">Co - Asst. Treasurer</option>
                      <option value="Chief Editor">Chief Editor</option>
                      <option value="President Program Coordinator">President Program Coordinator</option>
                      <option value="Club Performance Evaluator">Club Performance Evaluator</option>
                      <option value="Club Director">Club Director</option>
                      <option value="Team Head">Team Head</option>
                      <option value="Activity Director">Activity Director</option>
                      <option value="Past President">Past President</option>
                      <option value="Past Board Member">Past Board Member</option>
                      <option value="IT Department">IT Department</option>
                      <option value="Installation sub-committee member">Installation sub-committee member</option>
                      <option value="Reporting Sub-Committee">Reporting Sub-Committee</option>
                      <option value="Committee Member">Committee Member</option>
                    </select>
                  </div>

                  <div className="field">
                    <label className="field-label" htmlFor="add-member-student-id">Student ID</label>
                    <input
                      id="add-member-student-id"
                      type="text"
                      placeholder="Student ID"
                      value={studentId}
                      onChange={e => setStudentId(e.target.value)}
                      disabled={addLoading}
                    />
                  </div>

                  {addError && <div className="field-error">{addError}</div>}
                </div>

                <div className="modal-foot">
                  <button type="button" className="btn btn-ghost" onClick={() => setShowAddMember(false)} disabled={addLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={addLoading}>
                    {addLoading ? 'Adding…' : 'Add Member'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Change Role Modal */}
        {showChangeRole && changeRoleMember && (
          <div className="modal-overlay">
            <div className="modal-panel panel">
              <div className="modal-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span
                    style={{
                      width: 36, height: 36, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: 'var(--warning)'
                    }}
                  >
                    <FaExchangeAlt size={16} />
                  </span>
                  <h3 className="truncate">
                    Change Role{' '}
                    <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>
                      / {changeRoleMember.Name || changeRoleMember.name}
                    </span>
                  </h3>
                </div>
                <button className="icon-btn" onClick={closeChangeRoleModal} aria-label="Close change role dialog">
                  <FaTimes size={14} />
                </button>
              </div>

              <form onSubmit={handleChangeRoleSubmit}>
                <div className="modal-body">
                  <div className="field">
                    <label className="field-label" htmlFor="change-role-select">Select New Role</label>
                    <select
                      id="change-role-select"
                      value={changeRoleValue}
                      onChange={e => setChangeRoleValue(e.target.value)}
                      disabled={changeRoleLoading}
                    >
                      <option value="">Select Role</option>
                      <option value="System Admin">System Admin</option>
                      <option value="President">President</option>
                      <option value="Vice President">Vice President</option>
                      <option value="Secretary">Secretary</option>
                      <option value="Treasurer">Treasurer</option>
                      <option value="Co - Asst. Secretary">Co - Asst. Secretary</option>
                      <option value="Co - Asst. Treasurer">Co - Asst. Treasurer</option>
                      <option value="Chief Editor">Chief Editor</option>
                      <option value="Club Director">Club Director</option>
                      <option value="Team Head">Team Head</option>
                      <option value="Activity Director">Activity Director</option>
                      <option value="Past President">Past President</option>
                      <option value="President Program Coordinator">President Program Coordinator</option>
                      <option value="Club Performance Evaluator">Club Performance Evaluator</option>
                      <option value="Past Board Member">Past Board Member</option>
                      <option value="IT Department">IT Department</option>
                      <option value="Installation sub-committee member">Installation sub-committee member</option>
                      <option value="Reporting Sub-Committee">Reporting Sub-Committee</option>
                      <option value="Committee Member">Committee Member</option>
                      <option value="Club Member">Club Member</option>
                    </select>
                  </div>

                  {changeRoleError && <div className="field-error">{changeRoleError}</div>}
                </div>

                <div className="modal-foot">
                  <button type="button" className="btn btn-ghost" onClick={closeChangeRoleModal} disabled={changeRoleLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={changeRoleLoading}>
                    {changeRoleLoading ? 'Updating…' : 'Update Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <BanConfirmationModal />
        </main>
      </div>
    </>
  );
}
